import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  Breadcrumbs,
  Link,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const ImageSelector = ({ open, onClose, onSelect, currentValue, label }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const loadingRef = useRef(false);

  const loadImages = useCallback(async (path = '') => {
    // Prevenir requests duplicados
    if (loadingRef.current) {
      console.log('⏸️ [ImageSelector] Request already in progress, skipping...');
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      const response = await SitesService.listImages(path);
      setFolders(response.folders || []);
      setFiles(response.files || []);
    } catch (error) {
      console.error('Error loading images:', error);
      SnackbarUtils.error(
        error.response?.data?.message ||
        error.message ||
        t('error_loading_images') ||
        'Error al cargar las imágenes'
      );
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [t]);

  // Reset cuando se abre el modal
  useEffect(() => {
    if (open) {
      setCurrentPath('');
      setSearchTerm('');
      setSelectedImage(null);
      setFolders([]);
      setFiles([]);
      // Cargar imágenes después de resetear
      loadImages('');
    }
  }, [open, loadImages]);

  // Cargar imágenes cuando cambia el path (pero solo si el modal está abierto)
  useEffect(() => {
    if (open && !loadingRef.current) {
      loadImages(currentPath);
    }
  }, [currentPath, open, loadImages]);

  const navigateToFolder = (folderPath) => {
    setCurrentPath(folderPath);
  };

  const navigateUp = () => {
    if (!currentPath) return;
    const pathParts = currentPath.split('/');
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const getBreadcrumbs = () => {
    const parts = currentPath ? currentPath.split('/').filter(Boolean) : [];
    return [
      { name: t('images') || 'Imágenes', path: '' },
      ...parts.map((part, index) => ({
        name: part,
        path: parts.slice(0, index + 1).join('/')
      }))
    ];
  };

  const handleSelectImage = (image) => {
    setSelectedImage(image);
  };

  const handleConfirm = () => {
    if (selectedImage) {
      // Guardar el s3Key completo (ej: "sites/images/folder/image.jpg")
      // Si no hay s3Key, usar el path relativo pero asegurarse de que empiece con "sites/images/"
      let imagePath = selectedImage.s3Key;
      if (!imagePath) {
        // Si no hay s3Key, construir el path completo desde el path relativo
        const relativePath = selectedImage.path || '';
        imagePath = relativePath.startsWith('sites/images/') 
          ? relativePath 
          : `sites/images/${relativePath}`;
      }
      onSelect(imagePath);
      onClose();
    }
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {label || t('select_image') || 'Seleccionar Imagen'}
          </Typography>
          <TextField
            size="small"
            placeholder={t('search') || 'Buscar...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ width: 250 }}
          />
        </Box>
        {currentPath && (
          <Box sx={{ mt: 2 }}>
            <Breadcrumbs>
              {getBreadcrumbs().map((crumb, index) => (
                <Link
                  key={index}
                  component="button"
                  variant="body2"
                  onClick={() => setCurrentPath(crumb.path)}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: index === getBreadcrumbs().length - 1 ? 'none' : 'underline',
                    color: index === getBreadcrumbs().length - 1 ? 'text.primary' : 'primary.main'
                  }}
                >
                  {crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ minHeight: 400, maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {currentPath && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={navigateUp}
                sx={{ mb: 2 }}
              >
                {t('back') || 'Volver'}
              </Button>
            )}
            {filteredFolders.length === 0 && filteredFiles.length === 0 ? (
              <Alert severity="info">
                {t('no_images_found') || 'No hay imágenes en esta carpeta'}
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {/* Carpetas */}
                {filteredFolders.map((folder) => (
                  <Grid item xs={6} sm={4} md={3} key={folder.path}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <CardActionArea 
                        onClick={() => navigateToFolder(folder.path)}
                        sx={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          p: 2, 
                          minHeight: 140,
                          boxSizing: 'border-box'
                        }}
                      >
                        <FolderIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1, flexShrink: 0 }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mt: 1,
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto',
                            width: '100%',
                            maxHeight: '3em',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5
                          }}
                          title={folder.name}
                        >
                          {folder.name}
                        </Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}

                {/* Imágenes */}
                {filteredFiles.map((file) => (
                  <Grid item xs={6} sm={4} md={3} key={file.path}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: selectedImage?.path === file.path ? 2 : 1,
                        borderColor: selectedImage?.path === file.path ? 'primary.main' : 'divider',
                        overflow: 'hidden'
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handleSelectImage(file)}
                        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 180 }}
                      >
                        <CardMedia
                          component="img"
                          height="120"
                          image={file.url}
                          alt={file.name}
                          sx={{ 
                            objectFit: 'cover', 
                            width: '100%',
                            flexShrink: 0
                          }}
                        />
                        <Box sx={{ 
                          p: 1, 
                          flexGrow: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          minHeight: 50,
                          boxSizing: 'border-box',
                          overflow: 'hidden'
                        }}>
                          <Typography 
                            variant="caption" 
                            sx={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              hyphens: 'auto',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              width: '100%',
                              textAlign: 'center',
                              lineHeight: 1.4
                            }}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('cancel') || 'Cancelar'}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedImage}
        >
          {t('select') || 'Seleccionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageSelector;

